import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  jobTitle?: string | null;
  departmentCode?: string | null;
  isActive: boolean;
  emailConfirmed: boolean;
  mustChangePassword: boolean;
  roles: string[];
  permissions: string[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setSession: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clear: () => void;
  hasPermission: (permission?: string | null) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
}

/**
 * Authentication store. The access token, refresh token and the authenticated user's profile
 * (including effective permissions) are persisted to localStorage so a page reload keeps the
 * session. The API client reads the token from here and updates it after a silent refresh.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setSession: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true }),

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      clear: () => set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),

      hasPermission: (permission) => {
        if (!permission) return true;
        const user = get().user;
        return Boolean(user?.permissions.includes(permission));
      },

      hasAnyPermission: (...permissions) => {
        const user = get().user;
        return Boolean(user && permissions.some((p) => user.permissions.includes(p)));
      },

      hasAnyRole: (...roles) => {
        const user = get().user;
        return Boolean(user && roles.some((r) => user.roles.includes(r)));
      },
    }),
    { name: 'zsic-sbp-auth' },
  ),
);

/** Non-hook accessors for use outside React (e.g. the Axios interceptors). */
export const authStore = {
  getState: useAuthStore.getState,
  setState: useAuthStore.setState,
};
